import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import WebApp from '@twa-dev/sdk';
import { useTranslation, Trans } from 'react-i18next';
import { apiService } from '../services/api';
import useUserStore from '../stores/userStore';
import useInventoryStore from '../stores/inventoryStore';
import useCasesStore from '../stores/casesStore';
import CurrencyIcon from './CurrencyIcon';
import ReactPixel from 'react-facebook-pixel';
import NoTokensModal from './NoTokensModal';
import RouletteOverlay from './RouletteOverlay';
import { useSoundContext } from '../contexts/SoundContext';
import { primeSound, playMp3 } from '../utils/sound';
import useMusicStore from '../stores/musicStore';

const ITEM_HEIGHT = 300;

const MainContainer = styled.div`
  height: 100vh;
  width: 100vw;
  background: #23272f;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  padding: 0 8px;
  box-sizing: border-box;
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.35);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Loader = styled.div`
  width: 64px; height: 64px;
  border: 6px solid rgba(255,255,255,0.25);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const CountdownOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.6);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 800;
  font-size: 72px;
`;


const CaseView = () => {
	const { caseId } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const autoSpin = location.state?.autoSpin;
	const respinDrop = location.state?.respinDrop;
	const { t } = useTranslation();
	const { play } = useSoundContext();
	const musicIsRandom = useMusicStore(state => state.isRandom);
	const selectedMusicSrc = useMusicStore(state => state.selectedSrc);
	const setRandomSource = useMusicStore(state => state.setRandomSource);

	const [currentCase, setCurrentCase] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isOpening, setIsOpening] = useState(false);
	const [winningWeapon, setWinningWeapon] = useState(null);
	const [animationEnded, setAnimationEnded] = useState(false);
	const [randomizedItems, setRandomizedItems] = useState([]);
	const [showNoTokensModal, setShowNoTokensModal] = useState(false);
	const [showRoulette, setShowRoulette] = useState(false);
	const [countdown, setCountdown] = useState(0);
	const [spinStarted, setSpinStarted] = useState(false);

	const spinDurationMs = 8000;

	const user = useUserStore(state => state.user);
	const setUser = useUserStore.setState;
	const balance = user?.balance ? parseFloat(user.balance) : 0;
	const { addItem } = useInventoryStore();
	const cases = useCasesStore(state => state.cases);
	const fetchCases = useCasesStore(state => state.fetchCases);

	// Рулетку показываем только при старте открытия (handleOpen)

	useEffect(() => {
		if (WebApp.platform !== 'unknown') {
			WebApp.BackButton.show();
			WebApp.BackButton.onClick(() => navigate(`/case-contents/${caseId}`, { replace: true }));
		}
		return () => {
			if (WebApp.platform !== 'unknown') {
				WebApp.BackButton.hide();
				WebApp.BackButton.offClick();
			}
		};
	}, [navigate, caseId]);

	useEffect(() => {
		const fetchCase = async () => {
			const caseFromStore = cases.find(c => c.id === caseId);
			if (caseFromStore) {
				setCurrentCase(caseFromStore);
				const items = caseFromStore?.items || [];
				setRandomizedItems([...items].sort(() => Math.random() - 0.5));
				setIsLoading(false);
				return;
			}
			if (cases.length === 0) {
				await fetchCases();
				const updatedCase = cases.find(c => c.id === caseId);
				if (updatedCase) {
					setCurrentCase(updatedCase);
					const items = updatedCase?.items || [];
					setRandomizedItems([...items].sort(() => Math.random() - 0.5));
					setIsLoading(false);
					return;
				}
			}
			try {
				const response = await apiService.getCaseById(caseId);
				const caseData = response?.data?.data || response?.data;
				setCurrentCase(caseData);
				const items = caseData?.items || [];
				setRandomizedItems([...items].sort(() => Math.random() - 0.5));
			} catch {
				navigate('/');
			} finally {
				setIsLoading(false);
			}
		};
		fetchCase();
	}, [caseId, navigate, cases, fetchCases]);

	// Подготовка звука при возврате на экран, чтобы следующий старт был гарантирован
	useEffect(() => {
		primeSound('open');
	}, []);

	const handleOpen = useCallback(async () => {
		if (isOpening || !currentCase) return;
		if (balance < currentCase.tokens) {
			setShowNoTokensModal(true);
			return;
		}
		setIsOpening(true);
		if (WebApp.platform !== 'unknown') WebApp.BackButton.hide();
		window.history.pushState(null, '', window.location.href);
		window.history.replaceState(null, '', window.location.href);

		// 1) Определяем, какой src проигрывать, до await
		let toPlaySrc = null;
		if (musicIsRandom) {
			try {
				const ctx = require.context('../assets/music', false, /\.mp3$/);
				const keys = ctx.keys().filter(k => k.startsWith('./open-case_')).sort();
				if (keys.length > 0) {
					const any = keys[Math.floor(Math.random() * keys.length)];
					toPlaySrc = ctx(any);
				}
			} catch (_) { }
		} else {
			toPlaySrc = selectedMusicSrc;
		}
		// 2) Пытаемся запустить звук сразу (в рамках жеста)
		if (toPlaySrc) {
			playMp3(toPlaySrc, true);
		}

		try {
			let telegramId = WebApp.initDataUnsafe?.user?.id;
			const response = await apiService.openCase(currentCase.id, telegramId);
			if (!response.success) throw new Error(response.error || 'Ошибка при открытии кейса');
			const selectedWeapon = response.item;
			addItem(selectedWeapon);
			setWinningWeapon(selectedWeapon);
			ReactPixel.trackCustom('CaseOpened');
			const me = await apiService.getMe();
			if (me.status === 'success' && me.data) setUser({ user: me.data });
			// показываем рулетку, запускаем обратный отсчёт 3-2-1
			setShowRoulette(true);
			setSpinStarted(false);
			setCountdown(3);
			let step = 3;
			const timer = setInterval(() => {
				step -= 1;
				if (step <= 0) {
					clearInterval(timer);
					setCountdown(0);
					setSpinStarted(true);
				} else {
					setCountdown(step);
				}
			}, 1000);
		} catch (error) {
			setUser(prev => ({ user: { ...prev.user, balance: (parseFloat(prev.user.balance) || 0) + (currentCase.tokens || 0) } }));
			setIsOpening(false);
			setWinningWeapon(null);
			setAnimationEnded(false);
			if (WebApp.platform !== 'unknown') {
				WebApp.BackButton.show();
				WebApp.BackButton.onClick(() => navigate(`/case-contents/${caseId}`, { replace: true }));
			}
		}
	}, [isOpening, currentCase, balance, addItem, setUser, navigate, caseId, musicIsRandom, selectedMusicSrc]);

	useEffect(() => {
		// Автостарт анимации
		// Если пришли с респина (есть respinDrop), не списываем токены и не дергаем openCase —
		// просто запускаем рулетку с уже выбранным предметом
		if (autoSpin && randomizedItems.length > 0 && currentCase && !isOpening && !winningWeapon) {
			if (respinDrop) {
				// Показ обратного отсчета 3-2-1 перед стартом
				setShowRoulette(true);
				setSpinStarted(false);
				setCountdown(3);
				// Музыка при респине: используем те же правила (рандом/выбранный)
				try {
					let toPlaySrc = null;
					if (musicIsRandom) {
						const ctx = require.context('../assets/music', false, /\.mp3$/);
						const keys = ctx.keys().filter(k => k.startsWith('./open-case_')).sort();
						if (keys.length > 0) {
							const any = keys[Math.floor(Math.random() * keys.length)];
							toPlaySrc = ctx(any);
						}
					} else {
						toPlaySrc = selectedMusicSrc;
					}
					if (toPlaySrc) playMp3(toPlaySrc, true);
				} catch (_) { }
				let step = 3;
				const timer = setInterval(() => {
					step -= 1;
					if (step <= 0) {
						clearInterval(timer);
						setCountdown(0);
						setWinningWeapon(respinDrop);
						setSpinStarted(true);

					} else {
						setCountdown(step);
					}
				}, 1000);
			} else {
				handleOpen();
			}
		}
	}, [autoSpin, randomizedItems, currentCase, respinDrop, isOpening, winningWeapon, play, handleOpen]);

	useEffect(() => {
		if (winningWeapon && animationEnded && currentCase) {
			navigate(`/case-result/${winningWeapon.id}`, {
				state: { weapon: winningWeapon, caseId: currentCase.id, caseCost: currentCase.tokens, isRespin: Boolean(respinDrop) },
				replace: true,
			});
		}
	}, [winningWeapon, animationEnded, currentCase, navigate, respinDrop]);

	const handleShareReferral = () => {
		const botUsername = process.env.REACT_APP_BOT_USERNAME || '';
		const refLink = `https://t.me/${botUsername}?startapp=ref_${user?.ref || ''}`;
		if (WebApp.isVersionAtLeast && WebApp.isVersionAtLeast('6.0')) {
			WebApp.openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(refLink)}&text=${encodeURIComponent('Присоединяйся!')}`);
		} else {
			navigator.clipboard.writeText(refLink);
		}
	};


	if (isLoading) return <div>Загрузка...</div>;
	if (!currentCase) return <div>Ошибка</div>;

	return (
		<MainContainer>
			{/* Никакого списка на странице — только оверлей рулетки */}

			{showRoulette && (
				<RouletteOverlay
					items={randomizedItems}
					winnerItem={winningWeapon}
					durationMs={spinDurationMs}
					itemHeight={ITEM_HEIGHT}
					repeats={20}
					start={spinStarted}
					slowPhaseRatio={0.8}
					slowDistanceRatio={0.8}
					travelLoops={0}
					deterministic
					targetIndex={100}
					trackLength={120}
					onComplete={() => {
						setShowRoulette(false);
						setAnimationEnded(true);
						setIsOpening(false);
						if (WebApp.platform !== 'unknown') {
							WebApp.BackButton.show();
							WebApp.BackButton.onClick(() => navigate('/cases', { replace: true }));
						}
					}}
				/>
			)}

			{showRoulette && countdown > 0 && !spinStarted && (
				<CountdownOverlay>
					{countdown}
				</CountdownOverlay>
			)}

			{!autoSpin && (
				<div style={{ padding: '0 16px 24px 16px', background: 'transparent' }}>
					<button
						onClick={handleOpen}
						disabled={isOpening || !currentCase}
						style={{
							width: '100%', height: 56, borderRadius: 16, background: 'rgba(60,60,60,0.5)', color: '#fff',
							fontWeight: 'bold', fontSize: 22, border: 'none', cursor: isOpening ? 'not-allowed' : 'pointer',
							opacity: isOpening ? 0.7 : 1, transition: 'all 0.2s', boxShadow: '0 2px 16px 0 rgba(0,0,0,0.10)',
							marginBottom: 8, marginTop: 32, marginLeft: 'auto', marginRight: 'auto', display: 'block',
						}}
					>
						{isOpening ? (
							t('case.opening')
						) : (
							<Trans i18nKey="case.openFor" values={{ price: currentCase?.tokens }} components={{ currency: <CurrencyIcon size={16} /> }} />
						)}
					</button>
				</div>
			)}

			<NoTokensModal
				isOpen={showNoTokensModal}
				onClose={() => setShowNoTokensModal(false)}
				onBuy={() => { setShowNoTokensModal(false); navigate('/packages'); }}
				onInvite={() => { setShowNoTokensModal(false); handleShareReferral(); }}
				title={<Trans i18nKey="common.notEnoughTokens" components={{ currency: <CurrencyIcon size={16} /> }} />}
				desc={t('case.notEnoughTokensDesc')}
				buyLabel={t('case.buyTokens')}
				inviteLabel={t('case.inviteFriend')}
				closeLabel={t('case.close')}
			/>

			{isOpening && !showRoulette && (
				<LoadingOverlay>
					<Loader />
				</LoadingOverlay>
			)}
		</MainContainer>
	);
};

export default CaseView;

